import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ChevronDown, Shield, TrendingUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { fireEvent } from '@/lib/analytics';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { fuzzyScore } from '@/lib/fuzzySearch';
import { FAQ_DATA, FAQ_CATEGORIES, replaceFAQVariables, type FAQItem } from '@/content/faqs';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { getGridItemVariants } from '@/lib/animations';

// Lightweight, optimized parallax engine scoped to this file
type Axis = 'y' | 'x' | 'both';
interface ParallaxOptions {
  speed?: number;
  axis?: Axis;
  maxTranslate?: number;
  rootMargin?: string;
}
interface ParallaxItem {
  element: HTMLElement;
  speed: number;
  axis: Axis;
  maxTranslate: number;
  inView: boolean;
  lastTransform: string;
}

const parallaxItems = new Set<ParallaxItem>();
let parallaxIo: IntersectionObserver | null = null;
let parallaxRafId: number | null = null;
let parallaxRunning = false;
let prefersReducedMotion =
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function ensureParallaxIO(rootMargin: string) {
  if (parallaxIo) return;
  parallaxIo = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const item = [...parallaxItems].find((it) => it.element === entry.target);
        if (!item) continue;
        item.inView = entry.isIntersecting;
      }
      requestParallaxTick();
    },
    { root: null, threshold: 0, rootMargin },
  );
}

function onParallaxScroll() {
  requestParallaxTick();
}

function onParallaxResize() {
  requestParallaxTick();
}

function startParallax() {
  if (parallaxRunning) return;
  parallaxRunning = true;
  window.addEventListener('scroll', onParallaxScroll, { passive: true });
  window.addEventListener('resize', onParallaxResize, { passive: true });
  requestParallaxTick();
}

function stopParallax() {
  if (!parallaxRunning) return;
  window.removeEventListener('scroll', onParallaxScroll);
  window.removeEventListener('resize', onParallaxResize);
  parallaxRunning = false;
}

function requestParallaxTick() {
  if (parallaxRafId !== null) return;
  parallaxRafId = window.requestAnimationFrame(updateParallax);
}

function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max);
}

function updateParallax() {
  parallaxRafId = null;
  if (prefersReducedMotion) {
    parallaxItems.forEach((item) => {
      if (item.lastTransform !== '') {
        item.element.style.transform = '';
        item.lastTransform = '';
      }
    });
    return;
  }

  const viewportHeight = window.innerHeight || 0;
  const viewportWidth = window.innerWidth || 0;
  const centerY = viewportHeight / 2;
  const centerX = viewportWidth / 2;
  const farTop = -viewportHeight * 1.5;
  const farBottom = viewportHeight * 2.5;

  parallaxItems.forEach((item) => {
    const rect = item.element.getBoundingClientRect();

    if (rect.bottom < farTop || rect.top > farBottom) return;

    const elementCenterY = rect.top + rect.height / 2;
    const elementCenterX = rect.left + rect.width / 2;

    const dy = elementCenterY - centerY;
    const dx = elementCenterX - centerX;

    const translateY = clamp(-dy * item.speed, -item.maxTranslate, item.maxTranslate);
    const translateX = clamp(-dx * item.speed, -item.maxTranslate, item.maxTranslate);

    let transform = '';
    if (item.axis === 'y') {
      transform = `translate3d(0, ${translateY.toFixed(2)}px, 0)`;
    } else if (item.axis === 'x') {
      transform = `translate3d(${translateX.toFixed(2)}px, 0, 0)`;
    } else {
      transform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0)`;
    }

    if (transform !== item.lastTransform) {
      item.element.style.transform = transform;
      item.lastTransform = transform;
    }
  });
}

function useParallaxRef(options: ParallaxOptions = {}) {
  const {
    speed = 0.06,
    axis = 'y',
    maxTranslate = 40,
    rootMargin = '20% 0px 20% 0px',
  } = options;
  const elRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = elRef.current as HTMLElement | null;
    if (!el) return;

    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      prefersReducedMotion = mql.matches;
      requestParallaxTick();
    };
    mql.addEventListener?.('change', onChange);

    ensureParallaxIO(rootMargin);
    startParallax();

    const item: ParallaxItem = {
      element: el,
      speed,
      axis,
      maxTranslate,
      inView: false,
      lastTransform: '',
    };

    try {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      const vm = Math.max(0, Math.min(0.5, parseFloat(rootMargin.split(' ')[0]) / 100 || 0.2));
      const extendedTop = -vh * vm;
      const extendedBottom = vh * (1 + vm);
      item.inView = rect.bottom >= extendedTop && rect.top <= extendedBottom;
    } catch {
      item.inView = true;
    }

    parallaxItems.add(item);
    parallaxIo?.observe(el);

    el.style.willChange = 'transform';
    el.style.backfaceVisibility = 'hidden';
    el.style.transformStyle = 'preserve-3d';

    updateParallax();

    return () => {
      mql.removeEventListener?.('change', onChange);
      parallaxIo?.unobserve(el);
      parallaxItems.delete(item);
      el.style.willChange = '';
      el.style.backfaceVisibility = '';
      el.style.transformStyle = '';
      el.style.transform = '';

      if (parallaxItems.size === 0) {
        stopParallax();
      }
    };
  }, [axis, maxTranslate, rootMargin, speed]);

  return elRef;
}

// FAQ Group component to use hooks properly
function FAQGroup({
  category,
  categoryIndex,
  categoryFaqs,
  searchQuery,
  isPinned,
  openItems,
  toggleItem,
  variables,
  handleCTAClick,
}: {
  category: string;
  categoryIndex: number;
  categoryFaqs: FAQItem[];
  searchQuery: string;
  isPinned: boolean;
  openItems: Set<string>;
  toggleItem: (id: string, question: string) => void;
  variables: Record<string, string | undefined>;
  handleCTAClick: (cta: { to: string; label: string }, faqId: string) => void;
}) {
  const reveal = useRevealOnce({ amount: 0.3 });

  return (
    <motion.div
      ref={reveal.ref}
      id={category.toLowerCase().replace(/\s+/g, '-')}
      className="mb-12"
      variants={getGridItemVariants(categoryIndex)}
      initial="hidden"
      animate={reveal.controls}
    >
      <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
        {category}
        {isPinned && (
          <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
            Pinned
          </span>
        )}
      </h2>

      <div className="space-y-4">
        {categoryFaqs.map((faq) => {
          const isOpen = openItems.has(faq.id);
          const processedAnswer = replaceFAQVariables(faq.answer, variables);

          return (
            <Card key={faq.id} className="overflow-hidden transition-all duration-200 hover:shadow-md">
              <CardContent className="p-0">
                <button
                  onClick={() => toggleItem(faq.id, faq.question)}
                  className="w-full text-left p-6 flex items-start justify-between gap-4 hover:bg-accent/50 transition-colors"
                  style={{ minHeight: '44px' }}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${faq.id}`}
                  id={`faq-question-${faq.id}`}
                >
                  <div className="flex-1">
                    <span className="font-semibold text-lg">{faq.question}</span>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${
                      isOpen ? 'transform rotate-180' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      role="region"
                      id={`faq-answer-${faq.id}`}
                      aria-labelledby={`faq-question-${faq.id}`}
                    >
                      <div className="px-6 pb-6 space-y-4">
                        <p className="text-muted-foreground leading-relaxed">{processedAnswer}</p>

                        {faq.ctas && faq.ctas.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {faq.ctas.map((cta, idx) => (
                              <Button
                                key={idx}
                                asChild
                                variant="secondary"
                                size="sm"
                                onClick={() => handleCTAClick(cta, faq.id)}
                              >
                                <Link to={cta.to}>{cta.label}</Link>
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </motion.div>
  );
}

interface FAQsProps {
  context?: 'public' | 'account';
  domain?: string;
  binPrice?: number;
  leasePerMonth?: number;
}

const FAQs = ({ context = 'public', domain, binPrice, leasePerMonth }: FAQsProps) => {
  // Open "Top Objections" by default on desktop
  const [openItems, setOpenItems] = useState<Set<string>>(() => {
    const defaultOpen = new Set<string>();
    if (typeof window !== 'undefined' && window.innerWidth >= 768) {
      FAQ_DATA.filter((faq) => faq.category === 'Top Objections').forEach((faq) => {
        defaultOpen.add(faq.id);
      });
    }
    return defaultOpen;
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Use local FAQ data
  const faqs = FAQ_DATA;

  // Calculate dynamic values
  const leasePerDay = leasePerMonth ? (leasePerMonth / 30).toFixed(0) : undefined;
  const floorPct = '50';

  const variables = {
    domain: domain || 'this domain',
    binPrice: binPrice ? `$${binPrice.toLocaleString()}` : undefined,
    leasePerMonth: leasePerMonth ? `$${leasePerMonth}/mo` : undefined,
    leasePerDay: leasePerDay ? `$${leasePerDay}/day` : undefined,
    floorPct,
  };

  // Subtle randomized, layered gradient background (same as Home)
  const backgroundStyle = useMemo<React.CSSProperties>(() => {
    const rand = (min: number, max: number) => Math.random() * (max - min) + min;

    const hue = rand(205, 220);
    const saturation = rand(65, 78);
    const lightNearWhite = rand(96, 98);
    const lightBlue = rand(88, 92);
    const alphaSoft1 = rand(0.06, 0.1);
    const alphaSoft2 = rand(0.04, 0.08);

    const centerX = rand(30, 70);
    const centerY = rand(15, 45);

    const verticalAngle = 180;
    const horizontalAngle = 90;

    const color1 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, 1)`;
    const color2 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, 1)`;
    const tint1 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, ${alphaSoft1})`;
    const tint2 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, ${alphaSoft2})`;

    const backgroundImage = [
      `radial-gradient(ellipse at ${centerX}% ${centerY}%, ${tint1} 0%, transparent 60%)`,
      `linear-gradient(${verticalAngle}deg, #ffffff 0%, ${color2} 100%)`,
      `linear-gradient(${horizontalAngle}deg, ${tint2} 0%, transparent 50%, ${tint2} 100%)`,
    ].join(', ');

    return {
      backgroundImage,
      backgroundColor: '#ffffff',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover, cover, cover',
      backgroundAttachment: 'scroll',
    };
  }, []);

  // Parallax refs
  const heroTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 70 });
  const heroSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });

  // Fire view event
  useState(() => {
    fireEvent('faq_view', { context, path: window.location.pathname });
  });

  const categories = ['all', ...FAQ_CATEGORIES];

  const filteredFaqs = useMemo(() => {
    let filteredList = faqs.slice();

    // Filter by category
    if (selectedCategory !== 'all') {
      filteredList = filteredList.filter((faq) => faq.category === selectedCategory);
    }

    // Filter by search query with fuzzy matching
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();

      const scoredFaqs = filteredList.map((faq) => {
        const questionMatch = fuzzyScore(query, faq.question.toLowerCase());
        const answerMatch = fuzzyScore(query, faq.answer.toLowerCase());
        const categoryMatch = fuzzyScore(query, faq.category.toLowerCase());
        const tagsMatch = faq.tags
          ? Math.max(...faq.tags.map((tag) => fuzzyScore(query, tag.toLowerCase()).score))
          : 0;

        const totalScore =
          questionMatch.score * 3 + categoryMatch.score * 2 + tagsMatch * 2 + answerMatch.score;

        return {
          faq,
          score: totalScore,
          hasMatch:
            questionMatch.score > 0 ||
            answerMatch.score > 0 ||
            categoryMatch.score > 0 ||
            tagsMatch > 0,
        };
      });

      filteredList = scoredFaqs
        .filter((item) => item.hasMatch && item.score > 0.3)
        .sort((a, b) => b.score - a.score)
        .map((item) => item.faq);

      fireEvent('faq_search', { query, resultCount: filteredList.length });
    }

    return filteredList;
  }, [faqs, searchQuery, selectedCategory]);

  const toggleItem = (id: string, question: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        fireEvent('faq_expand', {
          faqId: id,
          q: question,
          context,
          path: window.location.pathname,
        });
      }
      return next;
    });
  };

  const handleCTAClick = (cta: { label: string; to: string; event: string }, faqId: string) => {
    fireEvent(cta.event, {
      faqId,
      ctaLabel: cta.label,
      path: window.location.pathname,
    });
  };

  // Group FAQs by category, maintaining category order
  const groupedFaqs = FAQ_CATEGORIES.reduce(
    (acc, category) => {
      const categoryFaqs = filteredFaqs.filter((faq) => faq.category === category);
      if (categoryFaqs.length > 0) {
        acc[category] = categoryFaqs;
      }
      return acc;
    },
    {} as Record<string, FAQItem[]>,
  );

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" style={backgroundStyle}>
      <Helmet>
        <title>
          {context === 'account' ? 'Support - Attain Domains' : 'FAQs - Attain Domains'}
        </title>
        <meta
          name="description"
          content={
            context === 'account'
              ? 'Get help with your Attain Domains account, leases, and purchases.'
              : 'Frequently asked questions about Attain Domains domain marketplace.'
          }
        />
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12 stagger-fade-1">
          <h1 ref={heroTitleRef as any} className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg">
            {context === 'account' ? 'Account Support' : 'Frequently Asked Questions'}
          </h1>
          <p ref={heroSubRef as any} className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed font-normal">
            {context === 'account'
              ? 'Get help with your account, leases, billing, and more'
              : 'Find answers to common questions about my domains and services'}
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
          <Input
            type="text"
            placeholder="Search FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-14 text-lg"
          />
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              onClick={() => setSelectedCategory(category)}
              className="capitalize text-sm"
            >
              {category === 'all' ? 'All Topics' : category}
            </Button>
          ))}
        </div>

        {/* Persuasive Badges */}
        {selectedCategory === 'all' && !searchQuery && (
          <div className="flex flex-wrap gap-3 mb-8 text-sm">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary font-medium">
              <TrendingUp className="w-4 h-4" />
              One-of-One Asset
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary font-medium">
              <Shield className="w-4 h-4" />
              Secure Payments
            </div>
          </div>
        )}

        {/* FAQ Groups */}
        {Object.entries(groupedFaqs).map(([category, categoryFaqs], categoryIndex) => {
          const isPinned = category === 'Top Objections';

          return (
            <FAQGroup
              key={category}
              category={category}
              categoryIndex={categoryIndex}
              categoryFaqs={categoryFaqs}
              searchQuery={searchQuery}
              isPinned={isPinned}
              openItems={openItems}
              toggleItem={toggleItem}
              variables={variables}
              handleCTAClick={handleCTAClick}
            />
          );
        })}

        {filteredFaqs.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No FAQs found matching your search.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQs;
